import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../../components/config/config";
import { useSelector } from "react-redux";
import {
  Business,
  Download,
  PlayArrow,
  Search,
  Person,
  Phone,
  LocationOn,
  CalendarToday,
  Receipt,
} from "@mui/icons-material";

// Excel columns for download - exact format as specified
const EXCEL_TEMPLATE_COLUMNS = [
  "CenterQr",
  "Client Id",
  "Service provided for",
  "Specific requirement",
  "service_provided_client_id",
  "Country code",
  "Mobile number",
  "Customer name",
  "House number",
  "Locality",
  "City",
  "State",
  "Pincode",
  "Alternate number",
  "Country",
  "Time preference type",
  "Specific date and time",
  "Ticket price",
  "Offer code",
  "Is paid ticket",
  "ticketCreatedBy",
  "service_provided_due_amount",
];

const PullbackPage = () => {
  const globalState = useSelector((state) => state.AdminReducer);
  const accessToken = globalState?.userTokens?.accessToken;

  // State management
  const [filterType, setFilterType] = useState("ALL");
  const [actionType, setActionType] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Fetch all clients for dropdown
  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}admin/get-all-clients`, {
        method: "GET",
        headers: {
          "x-access-token": accessToken,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch clients");
      }

      const data = await response.json();

      if (data.success === 1) {
        setClients(data.data || []);
      } else {
        throw new Error(data.message || "Failed to fetch clients");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setMessage({ type: "error", text: "Failed to fetch clients list" });
    } finally {
      setClientsLoading(false);
    }
  };

  // Fetch tickets based on filters
  const fetchTickets = async () => {
    if (!startDate || !endDate) {
      setMessage({
        type: "error",
        text: "Please select both start and end dates",
      });
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        ticketType: filterType.toUpperCase(),
        skip: "0",
        limit: "1000",
      });

      if (filterType === "CLIENT" && clientId) {
        // Find the selected client to get the _id
        const selectedClient = clients.find(
          (client) => client._id === clientId
        );
        if (selectedClient) {
          params.append("clientId", selectedClient._id);
        }
      }

      const response = await fetch(
        `${BASE_URL}admin/get-ticket-for-pull-back?${params}`,
        {
          method: "GET",
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch tickets");
      }

      const data = await response.json();

      // Extract tickets from the nested structure
      const fetchedTickets = data.data?.tickets || data.tickets || [];
      setTickets(fetchedTickets);

      // Use API message directly
      setMessage({
        type: "success",
        text: data.message || `Found ${fetchedTickets.length} tickets`,
      });
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setMessage({ type: "error", text: "Failed to fetch tickets" });
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  // Format tickets for Excel download in the exact specified format
  const formatTicketsForExcel = (tickets) => {
    return tickets.map((ticket) => ({
      CenterQr: "",
      "Client Id": ticket.client_id || "",
      "Service provided for": ticket.service_provided_for?.service_name || "",
      "Specific requirement": ticket.specific_requirement || "",
      service_provided_client_id: ticket.service_provided_client_id || "",
      "Country code": getCountryCode(ticket),
      "Mobile number":
        ticket.personal_details?.primary_phone?.mobile_number || "",
      "Customer name": ticket.personal_details?.name || "",
      "House number": ticket.address_details?.house_number || "",
      Locality: ticket.address_details?.locality || "",
      City: ticket.address_details?.city || "",
      State: ticket.address_details?.state || "",
      Pincode: ticket.address_details?.pincode || "",
      "Alternate number":
        ticket.personal_details?.alternate_phone?.mobile_number || "",
      Country: ticket.address_details?.country || "",
      "Time preference type":
        ticket.time_preference?.time_preference_type || "",
      "Specific date and time":
        ticket.time_preference?.specific_date_time || "",
      "Ticket price": ticket.ticket_price || "0",
      "Offer code": ticket.offers_applied?.offer_code || "",
      "Is paid ticket": getIsPaidTicket(ticket),
      ticketCreatedBy: ticket.ticket_created_by || "",
      service_provided_due_amount: ticket.service_provided_due_amount || "",
    }));
  };

  // Helper functions for ticket data mapping
  const getServiceType = (ticket) => {
    if (typeof ticket.service_provided_for === "object") {
      return ticket.service_provided_for.name || "";
    }
    return ticket.service_provided_for || "";
  };

  const getCountryCode = (ticket) => {
    return ticket.personal_details?.primary_phone?.country_code || "";
  };

  const getMobileNumber = (ticket) => {
    return ticket.personal_details?.primary_phone?.mobile_number || "";
  };

  const getCustomerName = (ticket) => {
    return (
      ticket.personal_details?.customer_name ||
      ticket.personal_details?.name ||
      ""
    );
  };

  const getAlternateNumber = (ticket) => {
    return ticket.personal_details?.alternate_phone?.mobile_number || "";
  };

  const getTimePreferenceType = (ticket) => {
    return (
      ticket.time_preference?.time_preference_type ||
      ticket.time_preference_type ||
      ""
    );
  };

  const getSpecificDateTime = (ticket) => {
    // This field doesn't seem to be in the API response
    return "";
  };

  const getOfferCode = (ticket) => {
    return ticket.offer_code || ticket.offers_applied?.offer_code || "";
  };

  const getIsPaidTicket = (ticket) => {
    return ticket.admin_setting?.is_paid ? "Yes" : "No";
  };

  const getServiceProvidedDueAmount = (ticket) => {
    // This field doesn't seem to be in the API response
    return "";
  };

  // Download tickets as Excel in the specified format
  const downloadExcel = () => {
    if (tickets.length === 0) {
      setMessage({ type: "warning", text: "No tickets to download" });
      return;
    }

    try {
      const formattedTickets = formatTicketsForExcel(tickets);

      // Create worksheet with the formatted data
      const worksheet = XLSX.utils.json_to_sheet(formattedTickets);

      // Create workbook and append worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tickets");

      // Download the file
      XLSX.writeFile(
        workbook,
        `pullback-tickets-${formatDate(new Date())}.xlsx`
      );

      setMessage({
        type: "success",
        text: "Excel file downloaded successfully with all specified fields",
      });
    } catch (error) {
      console.error("Error downloading Excel:", error);
      setMessage({ type: "error", text: "Failed to download Excel file" });
    }
  };

  // Execute pullback action (cancel/delete)
  const executePullback = async () => {
    if (!actionType) {
      setMessage({
        type: "error",
        text: "Please select an action (CANCELED or DELETED)",
      });
      return;
    }

    if (tickets.length === 0) {
      setMessage({ type: "warning", text: "No tickets to process" });
      return;
    }

    setLoading(true);
    try {
      const ticketIds = tickets.map((ticket) => ticket._id || ticket.id);

      const response = await fetch(
        `${BASE_URL}admin/cancel-pull-back-tickets?action=${actionType}`,
        {
          method: "PATCH",
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ticketIds }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        // Show API error message
        throw new Error(result.message || "Failed to execute pullback action");
      }

      // Check if the API returned success: 0
      if (result.success === 0) {
        throw new Error(result.message || "Action failed");
      }

      // Use API message directly
      setMessage({
        type: "success",
        text: result.message || `Action completed successfully`,
      });

      // Clear tickets after successful action
      setTickets([]);
    } catch (error) {
      console.error("Error executing pullback:", error);
      setMessage({
        type: "error",
        text: error.message || `Failed to ${actionType.toLowerCase()} tickets`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate one week ago from today
  useEffect(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    setStartDate(oneWeekAgo);
    setEndDate(new Date());
  }, []);

  // Fetch clients when component mounts and when filter type changes to CLIENT
  useEffect(() => {
    if (filterType === "CLIENT") {
      fetchClients();
    }
  }, [filterType]);

  // Get display fields for ticket preview
  const getDisplayFields = (ticket) => {
    return {
      "Ticket ID": ticket.ticket_id,
      "Client ID": ticket.created_by,
      "Service Type": getServiceType(ticket),
      "Specific Requirement": ticket.specific_requirement,
      "Customer Phone": getMobileNumber(ticket),
      "Customer Name": getCustomerName(ticket),
      City: ticket.address_details?.city,
      "Ticket Price": `₹${ticket.ticket_price || 0}`,
      Status: ticket.ticket_status,
      "Created At": new Date(ticket.createdAt).toLocaleDateString(),
    };
  };

  // Get status color for chip
  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "CANCELED":
        return "error";
      case "COMPLETED":
        return "success";
      default:
        return "default";
    }
  };

  // Get selected client name for display
  const getSelectedClientName = () => {
    if (filterType === "CLIENT" && clientId) {
      const selectedClient = clients.find((client) => client._id === clientId);
      return selectedClient
        ? `${selectedClient.client_name} (${selectedClient.client_id})`
        : "";
    }
    return "";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <Card className="mb-6 shadow-lg rounded-2xl border-0">
            <CardContent className="p-6 bg-gradient-to-r from-teal-500 to-teal-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h5" className="font-bold mb-2">
                    Pullback System
                  </Typography>
                  <Typography variant="body1" className="text-blue-100">
                    Cancel or delete pending requests that are one week older
                    than present date
                  </Typography>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-full">
                  <Business className="text-white text-3xl" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Message Alert */}
          {message.text && (
            <Alert
              severity={message.type}
              className="mb-6 shadow-lg rounded-xl"
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}

          {/* Filters Section */}
          <Card className="mb-6 shadow-lg rounded-2xl border-0">
            <CardContent className="p-6">
              <Typography
                variant="h6"
                className="font-semibold mb-4 text-gray-800 flex items-center"
              >
                <Search className="mr-2 text-blue-600" />
                Filter Tickets
              </Typography>

              <Grid container spacing={3}>
                {/* Filter Type Dropdown */}
                <Grid item xs={12} md={3}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    className="bg-white"
                  >
                    <InputLabel>Filter Type</InputLabel>
                    <Select
                      value={filterType}
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setClientId(""); // Reset client when filter type changes
                      }}
                      label="Filter Type"
                    >
                      <MenuItem value="ALL">All Tickets</MenuItem>
                      <MenuItem value="PUBLIC">Public</MenuItem>
                      <MenuItem value="CLIENT">Client</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Client Dropdown (only shown when CLIENT is selected) */}
                {filterType === "CLIENT" && (
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      className="bg-white"
                    >
                      <InputLabel>Select Client</InputLabel>
                      <Select
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        label="Select Client"
                        disabled={clientsLoading}
                        sx={{ minWidth: "220px" }}
                      >
                        {clientsLoading && (
                          <MenuItem disabled>
                            <CircularProgress size={20} className="mr-2" />
                            Loading clients...
                          </MenuItem>
                        )}
                        {clients.map((client) => (
                          <MenuItem key={client._id} value={client._id}>
                            <div className="flex items-center">
                              <Avatar className="w-6 h-6 mr-2 bg-blue-500 text-white text-xs">
                                {client.client_name?.charAt(0) || "C"}
                              </Avatar>
                              {client.client_name} ({client.client_id})
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Action Type Dropdown */}
                <Grid item xs={12} md={3}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    className="bg-white"
                  >
                    <InputLabel>Action Type</InputLabel>
                    <Select
                      value={actionType}
                      onChange={(e) => setActionType(e.target.value)}
                      label="Action Type"
                      sx={{ minWidth: "220px" }}
                    >
                      <MenuItem value="CANCELED">
                        <Chip label="CANCELED" color="warning" size="small" />
                      </MenuItem>
                      {/* <MenuItem value="DELETED">
                        <Chip label="DELETED" color="error" size="small" />
                      </MenuItem> */}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Date Range Section */}
              <Grid container spacing={3} className="mt-3">
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth className="bg-white" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth className="bg-white" />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Grid container spacing={2} className="mt-4">
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchTickets}
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Search />
                    }
                    className="rounded-lg px-6 py-2 shadow-lg"
                  >
                    Fetch Tickets
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={downloadExcel}
                    disabled={tickets.length === 0 || loading}
                    startIcon={<Download />}
                    className="rounded-lg px-6 py-2 border-2"
                  >
                    Download Excel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color={actionType === "DELETED" ? "error" : "warning"}
                    onClick={executePullback}
                    disabled={tickets.length === 0 || !actionType || loading}
                    startIcon={<PlayArrow />}
                    className="rounded-lg px-6 py-2 shadow-lg"
                  >
                    Execute {actionType || "Action"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Results Section */}
          {tickets.length > 0 && (
            <Card className="shadow-lg rounded-2xl border-0">
              <CardContent className="p-6">
                {/* Tickets Count */}
                <Box className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-100 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <Typography
                        variant="h5"
                        className="font-bold text-green-800"
                      >
                        {tickets.length} Tickets Found
                      </Typography>
                      <Typography variant="body2" className="text-green-600">
                        Ready for {actionType.toLowerCase()} action
                      </Typography>
                    </div>
                    <Chip
                      label={actionType}
                      color={actionType === "DELETED" ? "error" : "warning"}
                      variant="filled"
                    />
                  </div>
                </Box>

                {/* Tickets Preview */}
                <Typography
                  variant="h6"
                  className="font-semibold mb-4 text-gray-800 flex items-center"
                >
                  <Receipt className="mr-2 text-blue-600" />
                  Tickets Preview (First 5 entries)
                </Typography>

                <div className="space-y-4">
                  {tickets.slice(0, 5).map((ticket, index) => {
                    const displayFields = getDisplayFields(ticket);
                    return (
                      <Card
                        key={index}
                        variant="outlined"
                        className="rounded-xl hover:shadow-md transition-shadow"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center">
                              <Avatar className="w-10 h-10 bg-blue-100 text-blue-600 mr-3">
                                <Person />
                              </Avatar>
                              <div>
                                <Typography
                                  variant="h6"
                                  className="font-semibold"
                                >
                                  {displayFields["Customer Name"] || "N/A"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  className="text-gray-500"
                                >
                                  {displayFields["Ticket ID"]}
                                </Typography>
                              </div>
                            </div>
                            <Chip
                              label={displayFields["Status"]}
                              color={getStatusColor(displayFields["Status"])}
                              size="small"
                            />
                          </div>

                          <Divider className="my-3" />

                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <div className="flex items-center text-gray-600">
                                <Phone className="text-sm mr-2 text-blue-500" />
                                <Typography variant="body2">
                                  {displayFields["Customer Phone"] || "N/A"}
                                </Typography>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <div className="flex items-center text-gray-600">
                                <LocationOn className="text-sm mr-2 text-green-500" />
                                <Typography variant="body2">
                                  {displayFields["City"] || "N/A"}
                                </Typography>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <div className="flex items-center text-gray-600">
                                <Receipt className="text-sm mr-2 text-orange-500" />
                                <Typography
                                  variant="body2"
                                  className="font-medium"
                                >
                                  {displayFields["Ticket Price"]}
                                </Typography>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <div className="flex items-center text-gray-600">
                                <CalendarToday className="text-sm mr-2 text-purple-500" />
                                <Typography variant="body2">
                                  {displayFields["Created At"]}
                                </Typography>
                              </div>
                            </Grid>
                          </Grid>

                          {displayFields["Specific Requirement"] && (
                            <Box className="mt-3 p-2 bg-gray-50 rounded-lg">
                              <Typography
                                variant="body2"
                                className="text-gray-600"
                              >
                                <span className="font-semibold">
                                  Requirement:
                                </span>{" "}
                                {displayFields["Specific Requirement"]}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {tickets.length > 5 && (
                  <Box className="mt-4 text-center">
                    <Typography variant="body2" className="text-gray-500">
                      ... and {tickets.length - 5} more tickets
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default PullbackPage;
